import { apiFetch } from './api.js';

let sdkPromise = null;
let sdkKey = '';

function buildScriptUrl(key, libraries) {
  const u = new URL('https://maps.googleapis.com/maps/api/js');
  u.searchParams.set('key', key);
  const lib = String(libraries ?? 'places').trim();
  if (lib) u.searchParams.set('libraries', lib);
  u.searchParams.set('loading', 'async');
  return u.toString();
}

/**
 * Loads the Maps JavaScript API (Places when libraries default).
 * @param {string} apiKey
 * @param {{ libraries?: string }} [opts]
 */
export async function loadGoogleMapsAdminSdk(apiKey, opts = {}) {
  const key = String(apiKey || '').trim();
  if (!key) {
    throw Object.assign(new Error('no_key'), { code: 'NO_KEY' });
  }
  if (globalThis.google?.maps?.importLibrary || globalThis.google?.maps?.Map) return;

  const libraries = opts.libraries !== undefined ? opts.libraries : 'places';

  if (sdkPromise && sdkKey === key) {
    await sdkPromise;
    return;
  }

  sdkKey = key;
  sdkPromise = new Promise((resolve, reject) => {
    const prevGm = globalThis.gm_authFailure;
    let settled = false;

    const done = (fn) => {
      if (settled) return;
      settled = true;
      globalThis.gm_authFailure = prevGm;
      fn();
    };

    globalThis.gm_authFailure = () => {
      done(() => {
        try {
          prevGm?.();
        } catch {
          /* ignore */
        }
        reject(Object.assign(new Error('auth_failed'), { code: 'AUTH_FAILED' }));
      });
    };

    const script = document.createElement('script');
    script.async = true;
    script.dataset.dartsosGmaps = '1';
    script.src = buildScriptUrl(key, libraries);
    script.onerror = () => {
      done(() => reject(Object.assign(new Error('script_error'), { code: 'SCRIPT_ERROR' })));
    };
    script.onload = () => {
      const deadline = Date.now() + 20000;
      const poll = () => {
        if (globalThis.google?.maps?.importLibrary || globalThis.google?.maps?.Map) {
          done(() => resolve());
          return;
        }
        if (Date.now() > deadline) {
          done(() => reject(Object.assign(new Error('timeout'), { code: 'TIMEOUT' })));
          return;
        }
        setTimeout(poll, 40);
      };
      poll();
    };
    document.head.appendChild(script);
  }).catch((e) => {
    sdkPromise = null;
    sdkKey = '';
    throw e;
  });

  await sdkPromise;
}

export async function fetchAdminGoogleMapsApiKey() {
  const res = await apiFetch(`/api/admin/account-settings?_ts=${Date.now()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return '';
  return String(data?.settings?.googleMapsApiKey || '').trim();
}
